import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { runOnlineOrToast } from '../utils/networkGuard';

class ImageUploadService {
  private static instance: ImageUploadService;
  private readonly remedyBucketName = 'remedy-images';
  private readonly badgeBucketName = 'badge-images';
  private readonly avatarBucketName = 'member-avatars';

  private constructor() {}

  public static getInstance(): ImageUploadService {
    if (!ImageUploadService.instance) {
      ImageUploadService.instance = new ImageUploadService();
    }
    return ImageUploadService.instance;
  }

  async uploadRemedyImage(file: File, remedyName: string): Promise<string> {
    try {
      logger.info('📤 [ImageUpload] Fazendo upload da imagem:', file.name);

      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const timestamp = Date.now();
      const sanitizedName = remedyName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-');
      const extension = file.name.split('.').pop();
      const fileName = `${sanitizedName}-${timestamp}.${extension}`;

      logger.info('📝 [ImageUpload] Nome do arquivo:', fileName);

      const { data, error } = await runOnlineOrToast(async () =>
        supabase.storage
          .from(this.remedyBucketName)
          .upload(fileName, file, {
            cacheControl: '31536000',
            upsert: false
          })
      );

      if (error) {
        logger.error('❌ [ImageUpload] Erro no upload:', error);
        throw new Error(`Erro ao fazer upload: ${error.message}`);
      }

      if (!data?.path) {
        throw new Error('Upload realizado, mas não foi possível obter o caminho do arquivo.');
      }

      const { data: urlData } = supabase.storage
        .from(this.remedyBucketName)
        .getPublicUrl(data.path);

      if (!urlData?.publicUrl) {
        throw new Error('Não foi possível gerar a URL pública da imagem.');
      }

      logger.info('✅ [ImageUpload] URL pública:', urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error: any) {
      logger.error('❌ [ImageUpload] Erro:', error);
      throw error;
    }
  }

  async uploadMemberAvatar(file: File, memberName: string = 'member'): Promise<string> {
    try {
      logger.info('📤 [ImageUpload] Fazendo upload do avatar do membro:', file.name);

      const validation = this.validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const timestamp = Date.now();
      const sanitizedName = memberName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-');
      const extension = file.name.split('.').pop();
      const fileName = `${sanitizedName}-${timestamp}.${extension}`;

      const { data, error } = await runOnlineOrToast(async () =>
        supabase.storage
          .from(this.avatarBucketName)
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          })
      );

      if (error) {
        logger.error('❌ [ImageUpload] Erro no upload do avatar:', error);
        throw new Error(`Erro ao fazer upload do avatar: ${error.message}`);
      }

      if (!data?.path) {
        throw new Error('Upload realizado, mas não foi possível obter o caminho do avatar.');
      }

      const { data: urlData } = supabase.storage
        .from(this.avatarBucketName)
        .getPublicUrl(data.path);

      if (!urlData?.publicUrl) {
        throw new Error('Não foi possível gerar a URL pública do avatar.');
      }

      return urlData.publicUrl;
    } catch (error: any) {
      logger.error('❌ [ImageUpload] Erro:', error);
      throw error;
    }
  }

  async deleteRemedyImage(imageUrl: string): Promise<void> {
    try {
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];

      if (!fileName) {
        logger.warn('⚠️ [ImageUpload] URL inválida, não é possível deletar');
        return;
      }

      logger.info('🗑️ [ImageUpload] Deletando imagem:', fileName);
      const { error } = await runOnlineOrToast(async () =>
        supabase.storage
          .from(this.remedyBucketName)
          .remove([fileName])
      );

      if (error) {
        logger.error('❌ [ImageUpload] Erro ao deletar:', error);
        throw new Error(`Erro ao deletar imagem: ${error.message}`);
      }

      logger.info('✅ [ImageUpload] Imagem deletada com sucesso');
    } catch (error: any) {
      logger.error('❌ [ImageUpload] Erro:', error);
    }
  }

  async deleteMemberAvatar(avatarUrl: string | null | undefined): Promise<void> {
    if (!avatarUrl || avatarUrl.startsWith('/Imagens/') || avatarUrl.startsWith('data:')) {
      return;
    }

    try {
      const sanitizedUrl = decodeURIComponent(avatarUrl);
      const urlWithoutQuery = sanitizedUrl.split('?')[0];
      const urlParts = urlWithoutQuery.split('/');
      const fileName = urlParts[urlParts.length - 1];

      if (!fileName) {
        logger.warn('⚠️ [ImageUpload] URL de avatar inválida, não é possível deletar');
        return;
      }

      logger.info('🗑️ [ImageUpload] Deletando avatar do membro:', fileName);
      const { error } = await runOnlineOrToast(async () =>
        supabase.storage
          .from(this.avatarBucketName)
          .remove([fileName])
      );

      if (error) {
        logger.error('❌ [ImageUpload] Erro ao deletar avatar:', error);
        throw new Error(`Erro ao deletar avatar: ${error.message}`);
      }

      logger.info('✅ [ImageUpload] Avatar deletado com sucesso');
    } catch (error: any) {
      logger.error('❌ [ImageUpload] Erro ao remover avatar:', error);
    }
  }

  validateImageFile(file: File): { valid: boolean; error?: string } {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      return {
        valid: false,
        error: 'Tipo de arquivo inválido. Use JPG, PNG, WEBP ou GIF.'
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Arquivo muito grande. Tamanho máximo: 5MB.'
      };
    }

    return { valid: true };
  }

  async uploadBadgeImage(file: File, badgeName: string): Promise<string> {
    try {
      logger.info('📤 [ImageUpload] Fazendo upload da imagem do badge:', file.name);

      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Tipo de arquivo inválido. Use JPG, PNG, WEBP ou GIF.');
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Tamanho máximo: 5MB.');
      }

      const timestamp = Date.now();
      const sanitizedName = badgeName
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-');
      const extension = file.name.split('.').pop();
      const fileName = `${sanitizedName}-${timestamp}.${extension}`;

      logger.info('📝 [ImageUpload] Nome do arquivo:', fileName);

      const { data, error } = await runOnlineOrToast(async () =>
        supabase.storage
          .from(this.badgeBucketName)
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          })
      );

      if (error) {
        logger.error('❌ [ImageUpload] Erro no upload:', error);
        throw new Error(`Erro ao fazer upload: ${error.message}`);
      }

      if (!data?.path) {
        throw new Error('Upload realizado, mas não foi possível obter o caminho do arquivo.');
      }

      const { data: urlData } = supabase.storage
        .from(this.badgeBucketName)
        .getPublicUrl(data.path);

      if (!urlData?.publicUrl) {
        throw new Error('Não foi possível gerar a URL pública da imagem.');
      }

      logger.info('✅ [ImageUpload] URL pública:', urlData.publicUrl);
      return urlData.publicUrl;
    } catch (error: any) {
      logger.error('❌ [ImageUpload] Erro:', error);
      throw error;
    }
  }

  async deleteBadgeImage(imageUrl: string): Promise<void> {
    try {
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];

      if (!fileName) {
        logger.warn('⚠️ [ImageUpload] URL inválida, não é possível deletar');
        return;
      }

      logger.info('🗑️ [ImageUpload] Deletando imagem do badge:', fileName);
      const { error } = await runOnlineOrToast(async () =>
        supabase.storage
          .from(this.badgeBucketName)
          .remove([fileName])
      );

      if (error) {
        logger.error('❌ [ImageUpload] Erro ao deletar:', error);
        throw new Error(`Erro ao deletar imagem: ${error.message}`);
      }

      logger.info('✅ [ImageUpload] Imagem deletada com sucesso');
    } catch (error: any) {
      logger.error('❌ [ImageUpload] Erro:', error);
    }
  }

  async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }
}

export default ImageUploadService;
